import {
  useMantineTheme,
  Text,
  Title,
  Stack,
  createStyles,
  getStylesRef,
  MediaQuery,
} from '@mantine/core';
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSessionContext, useSupabaseClient } from '@supabase/auth-helpers-react';
import { modals } from '@mantine/modals';
import { useIdle } from '@mantine/hooks';
import { GridContextProvider, GridDropZone } from '../../src/GridDnD';
import { useMeasure } from '../../src/GridDnD/use-measure';
import { delay, fillText, noDragStyles } from '../../src/Utils';
import MemoRow, { ImageGallery, TierItem } from './TierRow';
import { Database } from '../../types/supabase';
import { TierData } from '../GradientPicker/GradientPicker';
import { GridSettings } from '../../src/GridDnD/grid-types';
import { SatoshiVariable } from '../../pages/_app';

export interface TierItemData {
  id: string;
  src: string;
  name: string;
}

const useStyles = createStyles((theme) => ({
  tierGrid: {},

  brandingBox: {
    position: 'absolute',
    bottom: 3,
    right: 3,
    borderRadius: 8,
    border: `2px solid ${
      theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[5]
    }`,
    transition: 'all 0.2s ease',
    gap: 0,
    [`&:hover .${getStylesRef('boxClose')}`]: {
      display: 'block',
    },
    cursor: 'default',

    [`&:hover`]: {
      transform: 'scale(0.95)',
    },
    userSelect: 'none',
  },

  brandingBoxClose: {
    position: 'absolute',
    top: 2,
    right: 2,
    ref: getStylesRef('boxClose'),
    display: 'none',
  },
}));

interface GridData extends GridSettings {
  galleryBoxes: number;
}

export default function TierGrid({
  tiersData,
  tierId,
  tiersContainerRef,
  dataLoaded,
  savedTierItems,
  onDataUpdated,
  isShared,
  sharedUserName,
  removeSaveIDFromURL,
  onShareableLink,
  onShare,
  isMobile,
  templateUpdateItems,
}: {
  tiersData: TierData[];
  tierId: number;
  tiersContainerRef: RefObject<HTMLDivElement>;
  dataLoaded: boolean;
  savedTierItems: TierItemData[][];
  onDataUpdated: (num: number) => void;
  isShared: boolean;
  sharedUserName: string | null;
  onShare: (string: string) => void;
  removeSaveIDFromURL: () => void;
  onShareableLink: (justUpdate?: boolean) => void;
  isMobile: boolean;
  templateUpdateItems: (items: TierItemData[][]) => void;
}) {
  const theme = useMantineTheme();
  const [tierItems, setTierItems] = useState<TierItemData[][]>(savedTierItems);
  const [showShared, setShowShare] = useState(true);
  const isMounted = useRef(false);

  const [grid, setGrid] = useState<GridData>(
    isMobile
      ? {
          boxesPerRow: 0,
          galleryBoxes: 0,
          columnWidth: 75,
          rowHeight: 85,
        }
      : {
          boxesPerRow: 0,
          galleryBoxes: 0,
          columnWidth: 100,
          rowHeight: 125,
        }
  );

  const supabase = useSupabaseClient<Database>();
  const { session } = useSessionContext();
  const { classes } = useStyles();

  const onDisabledDrag = useCallback(() => {
    modals.openConfirmModal({
      title: `You are viewing a tier list shared by ${sharedUserName}`,
      centered: true,
      children: (
        <Text size="sm">Click Confirm to be navigated away to create your own tier list.</Text>
      ),
      labels: { confirm: 'Confirm', cancel: 'Cancel' },
      onCancel: () => console.log('Cancel'),
      onConfirm: removeSaveIDFromURL,
    });
  }, [removeSaveIDFromURL, sharedUserName]);

  const { bounds, remeasure } = useMeasure(tiersContainerRef);

  useEffect(() => {
    if (bounds.width === 0) {
      return;
    }
    if (!isMobile) {
      const newGrid: GridData = {
        boxesPerRow: Math.floor((bounds.width - 125 - 20) / 100),
        galleryBoxes: Math.floor((bounds.width - 20) / 100),
        rowHeight: 125,
        columnWidth: 100,
      };
      setGrid(newGrid);
    } else {
      const newGrid: GridData = {
        boxesPerRow: Math.floor((bounds.width - 95) / 75),
        galleryBoxes: Math.floor((bounds.width - 20) / 75),
        rowHeight: 85,
        columnWidth: 75,
      };
      setGrid(newGrid);
    }
  }, [bounds, isMobile]);

  useEffect(() => {
    console.log(savedTierItems);
    const showShare =
      JSON.parse(localStorage.getItem(`ShowShareTierData-${tierId}`) ?? 'true') === true;
    setShowShare(showShare);
  }, [savedTierItems]);

  const tierArray = useMemo(
    () => tierItems.flat().sort((a, b) => a.id.localeCompare(b.id)),
    [tierItems]
  );

  const sharePending = React.useRef(false);
  const idle = useIdle(3000);

  useEffect(() => {
    if (idle) {
      if (sharePending.current) {
        sharePending.current = false;
        onShareableLink(true);
      }
    }
  }, [idle, onShareableLink]);

  const onItemsUpdate = useCallback(
    (newResult: TierItemData[][]) => {
      console.log('on update within GRID');
      if (!dataLoaded) {
        return;
      }

      if (!isMounted.current) {
        isMounted.current = true;
        return;
      }

      const savedItems = newResult.map((row) => row.map((item) => item.id));

      try {
        localStorage.setItem(`TierData-${tierId}`, JSON.stringify(savedItems));
      } catch (error) {
        console.error(error);
      }

      if (session?.user.id) {
        (async () => {
          const response = await supabase
            .from('tier_lists_save')
            .upsert({ template_id: tierId, user_id: session?.user.id, data: savedItems })
            .select('save_id')
            .single();
          sharePending.current = true;
          //   onShareableLink(true);
          sharePending.current = true;
          if (response && response.data) {
            const { save_id } = response.data;
            onDataUpdated(save_id);
          }
        })();
      }

      templateUpdateItems(newResult);

      if (newResult.at(-1)?.length === 0 && showShared) {
        try {
          localStorage.setItem(`ShowShareTierData-${tierId}`, 'false');
        } catch (error) {
          console.error(error);
          setShowShare(false);
          return;
        }
        setShowShare(false);
        delay(250).then(() => {
          onShare('link');
        });
      }
    },
    [
      dataLoaded,
      onDataUpdated,
      onShare,
      onShareableLink,
      session?.user.id,
      showShared,
      supabase,
      tierId,
    ]
  );

  const GridMemo = React.useMemo(
    () => (
      <GridContextProvider
        isMobile={isMobile}
        dragEnabled={!isShared}
        onItemsUpdate={onItemsUpdate}
        initTierItems={tierItems}
        onDisabledDrag={onDisabledDrag}
      >
        <div
          id="tierGrid"
          className={`${classes.tierGrid} container`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: 120,
          }}
        >
          <div
            ref={tiersContainerRef}
            style={{
              position: 'relative',
            }}
          >
            <>
              {tiersData.map((tierData, idx) => (
                <MemoRow
                  key={tierData.id}
                  boxesPerRow={grid.boxesPerRow}
                  rowHeight={grid.rowHeight}
                  tierData={tierData}
                  idx={idx}
                  items={[]}
                  boxWidth={grid.columnWidth}
                  isMobile={isMobile}
                />
              ))}
              <Stack
                className={classes.brandingBox}
                align="center"
                justify="center"
                bg={theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0]}
                w={grid.columnWidth - 5}
                h={grid.columnWidth - 5}
                p={isMobile ? 1 : 2}
              >
                <Text fw={700} color="dimmed" size={isMobile ? 10 : 'xs'}>
                  {' '}
                  Made with
                </Text>

                <div>
                  <Title
                    color={theme.colorScheme === 'dark' ? 'violet.3' : 'violet.7'}
                    fw={800}
                    size={isMobile ? 12 : 18}
                    sx={{
                      lineBreak: 'anywhere',
                    }}
                  >
                    LomoList
                  </Title>
                  <Title
                    color={theme.colorScheme === 'dark' ? 'violet.3' : 'violet.7'}
                    fw={800}
                    ta="center"
                    size={isMobile ? 10 : 16}
                    sx={{
                      lineBreak: 'anywhere',
                    }}
                  >
                    .com
                  </Title>
                </div>
              </Stack>
            </>
          </div>
          <>
            {!isMobile && (
              <Text
                sx={{
                  userSelect: 'none',
                  MozUserSelect: 'none',
                  ...noDragStyles,
                }}
                mt="lg"
                mb="sm"
                size="lg"
                fw={700}
              >
                Image Gallery
              </Text>
            )}
            <ImageGallery
              key="imageGallery"
              boxesPerRow={grid?.galleryBoxes ?? 0}
              rowHeight={grid?.rowHeight ?? 0}
              idx={tiersData.length}
              items={grid.boxesPerRow === 0 ? [] : tierArray}
              boxWidth={grid?.columnWidth ?? 0}
              isMobile={isMobile}
            />
          </>
        </div>
      </GridContextProvider>
    ),
    [
      isMobile,
      isShared,
      onItemsUpdate,
      grid,
      tierItems,
      onDisabledDrag,
      classes.tierGrid,
      classes.brandingBox,
      tiersContainerRef,
      tiersData,
      theme.colorScheme,
      theme.colors.dark,
      theme.colors.gray,
      tierArray,
    ]
  );

  return <>{GridMemo}</>;
}
